import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Stack,
  TextField,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import { useProject } from '../contexts/ProjectContext';
import { getDashboardMetrics, generateInstantIdeas, createArticle } from '../services/api';
import { useNavigate } from 'react-router-dom';

const DEFAULT_TOPICS = [
  "How to increase productivity at work",
  "Essential tips for remote work success",
  "Building healthy habits that last"
];

const ARTICLE_TYPES = [
  { 
    value: 'article', 
    label: 'Article',
    subtypes: [
      { value: 'how_to', label: 'How-To Guide' },
      { value: 'list_article', label: 'List Article' },
      { value: 'review', label: 'Review' },
      { value: 'opinion', label: 'Opinion' },
      { value: 'news', label: 'News' },
      { value: 'case_study', label: 'Case Study' }
    ]
  },
  { 
    value: 'short_social_post', 
    label: 'Short Social',
    subtypes: [
      { value: 'instagram_post', label: 'Instagram Post' },
      { value: 'facebook_post', label: 'Facebook Post' },
      { value: 'linkedin_short', label: 'LinkedIn Short' },
      { value: 'threads_post', label: 'Threads Post' },
      { value: 'x_post', label: 'X Post' }
    ]
  },
  { 
    value: 'long_social_post', 
    label: 'Social Thread',
    subtypes: [
      // { value: 'linkedin_article', label: 'LinkedIn Article' },
      // { value: 'facebook_long', label: 'Facebook Long' },
      // { value: 'medium_story', label: 'Medium Story' },
      { value: 'x_thread', label: 'X Thread' }
    ]
  },
  // { 
  //   value: 'image_carousel', 
  //   label: 'Image Carousel',
  //   subtypes: [
  //     { value: 'instagram_carousel', label: 'Instagram Carousel' },
  //     { value: 'linkedin_carousel', label: 'LinkedIn Carousel' },
  //     { value: 'facebook_carousel', label: 'Facebook Carousel' }
  //   ]
  // },
  // { 
  //   value: 'short_video', 
  //   label: 'Short Video',
  //   subtypes: [
  //     { value: 'tiktok', label: 'TikTok' },
  //     { value: 'instagram_reel', label: 'Instagram Reel' },
  //     { value: 'youtube_short', label: 'YouTube Short' }
  //   ]
  // },
  // { 
  //   value: 'long_video', 
  //   label: 'Long Video',
  //   subtypes: [
  //     { value: 'youtube_video', label: 'YouTube Video' },
  //     { value: 'vimeo_video', label: 'Vimeo Video' },
  //     { value: 'course_video', label: 'Course Video' }
  //   ]
  // }
];

const Dashboard = () => {
  const { selectedProject } = useProject();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topic, setTopic] = useState('');
  const [type, setType] = useState('article');
  const [subtype, setSubtype] = useState('');
  const [suggestedTopics, setSuggestedTopics] = useState(DEFAULT_TOPICS);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleCreateArticle = async () => {
    if (topic.trim()) {
      try {
        setLoading(true);
        const newArticle = await createArticle({ 
          title: topic.trim(), 
          topic: topic.trim(),
          project_id: selectedProject.id,
          type,
          subtype: subtype || null  // Add subtype to the request
        });

        // Track article creation event
        window.gtag('event', 'article_started', {
          'event_category': 'conversion',
          'event_label': 'article_started',
          'value': 10,
          'currency': 'EUR',
          'article_type': type,
          'article_subtype': subtype || 'general'
        });

        navigate(`/create-article?article_id=${newArticle.id}`);
      } catch (err) {
        console.error('Error creating article:', err);
        enqueueSnackbar('Error creating article: ' + err.message, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTopicClick = (suggestedTopic) => {
    setTopic(suggestedTopic);
    setSuggestedTopics([]); // Clear suggestions after selection
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleCreateArticle();
    }
  };

  // Debounced function to fetch topic suggestions
  const debouncedFetchSuggestions = useCallback(
    debounce(async (input) => {
      if (!input.trim() || !selectedProject) return;

      try {
        setLoadingSuggestions(true);
        const response = await generateInstantIdeas(selectedProject.id, input);
        setSuggestedTopics(response.topics.map(t => t.topic));
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setSuggestedTopics([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 1000),
    [selectedProject]
  );

  // Handle topic input changes
  const handleTopicChange = (e) => {
    const newTopic = e.target.value;
    setTopic(newTopic);
    
    if (newTopic.trim()) {
      debouncedFetchSuggestions(newTopic);
    } else {
      setSuggestedTopics(DEFAULT_TOPICS); // Reset to default topics when input is cleared
    }
  };

  // Add handler for type change
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    setSubtype(''); // Reset subtype when type changes
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!selectedProject) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getDashboardMetrics(selectedProject.id);
        setMetrics(data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedProject]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchSuggestions.cancel();
    };
  }, [debouncedFetchSuggestions]);

  if (!selectedProject) {
    return (
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3 
      }}>
        <Typography variant="h5" align="center">
          Please select a project to view the dashboard
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3 
      }}>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasArticles = metrics.total_content > 0;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 2, sm: 3 },
      bgcolor: theme => theme.palette.background.default
    }}>
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: '800px',
        mx: 'auto',
        width: '100%',
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            fontWeight: 500,
            color: 'text.primary',
            mb: 2,
            textAlign: 'center'
          }}
        >
          What would you like to write about?
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 4,
            color: 'text.secondary',
            textAlign: 'center'
          }}
        >
          Enter your topic and let AI help you create amazing content
        </Typography>
        
        <Paper 
          elevation={2}
          sx={{ 
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            maxWidth: '48rem',
            mx: 'auto',
            width: '100%'
          }}
        >
          {/* Top row with input and send button */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'flex-end',
            gap: 1,
            width: '100%'
          }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={topic}
              onChange={handleTopicChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter your topic here..."
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { 
                  fontSize: '0.9375rem',
                  padding: 0,
                  '& textarea': {
                    padding: '6px 0'
                  }
                }
              }}
              sx={{ 
                flex: 1,
                '& textarea::placeholder': {
                  opacity: 0.7,
                  color: 'text.secondary'
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleCreateArticle}
              disabled={!topic.trim()}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: '50%',
                p: 0,
                minHeight: 'unset'
              }}
            >
              <AutoFixHighIcon fontSize="small" />
            </Button>
          </Box>

          {/* Bottom row with type and subtype selectors */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 0.5,
            mt: 0.5
          }}>
            {/* Type selector group */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flex: { xs: 1, sm: 'none' }
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  minWidth: '35px'
                }}
              >
                Type:
              </Typography>
              <FormControl 
                size="small"
                sx={{ 
                  flex: 1,
                  minWidth: 100,
                  ml: 0.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'action.hover',
                    '& fieldset': {
                      border: 'none'
                    }
                  }
                }}
              >
                <Select
                  value={type}
                  onChange={handleTypeChange}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiSelect-select': {
                      py: 0.5,
                      px: 1.25,
                    }
                  }}
                >
                  {ARTICLE_TYPES.map((type) => (
                    <MenuItem 
                      key={type.value} 
                      value={type.value}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Subtype selector group */}
            {type && ARTICLE_TYPES.find(t => t.value === type)?.subtypes && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flex: { xs: 1, sm: 'none' }
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    minWidth: '35px',
                    ml: 0.5
                  }}
                >
                  For:
                </Typography>
                <FormControl 
                  size="small"
                  sx={{ 
                    flex: 1,
                    minWidth: 100,
                    ml: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      backgroundColor: 'action.hover',
                      '& fieldset': {
                        border: 'none'
                      }
                    }
                  }}
                >
                  <Select
                    value={subtype}
                    onChange={(e) => setSubtype(e.target.value)}
                    size="small"
                    displayEmpty
                    sx={{
                      fontSize: '0.75rem',
                      height: 28,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        px: 1.25,
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                      <em>General</em>
                    </MenuItem>
                    {ARTICLE_TYPES.find(t => t.value === type)?.subtypes.map((subtype) => (
                      <MenuItem 
                        key={subtype.value} 
                        value={subtype.value}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {subtype.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </Paper>

        {loadingSuggestions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : suggestedTopics.length > 0 ? (
          <>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 2,
                color: 'text.secondary',
                textAlign: 'center'
              }}
            >
              {topic ? 'Suggested topics:' : 'Popular topics to get you started:'}
            </Typography>
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
                maxWidth: '48rem',
                mx: 'auto'
              }}
            >
              {suggestedTopics.map((suggestedTopic) => (
                <Chip
                  key={suggestedTopic}
                  label={suggestedTopic}
                  onClick={() => handleTopicClick(suggestedTopic)}
                  sx={{ 
                    m: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      cursor: 'pointer'
                    }
                  }}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default Dashboard; 